import { Box } from "@mui/material";
import React, { ReactNode } from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import { Card } from "./Card.styled";
import { HeaderText } from "../text/HeaderText.styled";
import { CardButton } from "../buttons/CardButton.styled";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BodyText } from "../text/BodyText.styled";
import { IconButton } from "../buttons/IconButton.styled";
import PaneTooltip, { INFO_TOOLTIP_DELAY_MS } from "../text/PaneTooltip";
import { useDialog } from "../../context/DialogContext";
import { DialogDescription } from "../dialog/Dialog";
import {
  REMOVE_ACTION,
  openDatasourceTooltip,
  refreshDatasourceTooltip,
  removeDatasourceDescription,
  removeDatasourceTitle,
  removeDatasourceTooltip,
  updateDatasourceTooltip,
} from "../../constants";
import { ediphiPalette } from "../../styles/colors";

/**
 * Either a plain label, or a rendered node (a partner logo) plus the `name` the delete dialog needs
 * — that copy is a sentence and cannot render a node. Expressed as a union so the requirement is a
 * compile error at the call site rather than a comment.
 */
type DatasourceCardHeader =
  | { text: string; icon?: SvgIconComponent; name?: string }
  | { text: ReactNode; name: string; icon?: SvgIconComponent };

interface DatasourceCardLayoutProps {
  header: DatasourceCardHeader;
  card: {
    text: string;
    subtext: string;
  };
  onCardClick?: () => void;
  onLaunch?: () => void;
  onClose: () => void;
  onRefresh?: () => void;
  /** Info shown in the delete dialog. */
  deleteNotice: string;
  children?: ReactNode;
}

const DatasourceCardLayout = ({
  header,
  card,
  onCardClick,
  onLaunch,
  onClose,
  onRefresh,
  deleteNotice,
  children,
}: DatasourceCardLayoutProps) => {
  const HeaderIcon = header.icon;
  const { showDialog, hideDialog } = useDialog();
  // The union guarantees one of these is present; TS cannot narrow on `text` because ReactNode
  // subsumes string, so the read stays defensive even though the call sites are checked.
  const datasourceLabel = header.name ?? (typeof header.text === "string" ? header.text : "");

  return (
    <Box>
      {/* <Card sx={{ flexDirection: "column", gap: 1.5, border: "1px solid #CDCDCD" }}> */}
      <Card sx={{ flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {HeaderIcon && <HeaderIcon sx={{ fontSize: "18px" }} />}
            <HeaderText sx={{ fontSize: "12px" }}>{header.text}</HeaderText>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {onRefresh && (
              <PaneTooltip title={refreshDatasourceTooltip(datasourceLabel)}>
                <IconButton onClick={onRefresh}>
                  <RefreshIcon />
                </IconButton>
              </PaneTooltip>
            )}
            {onLaunch && (
              <PaneTooltip title={openDatasourceTooltip(datasourceLabel)}>
                <IconButton onClick={onLaunch}>
                  <OpenInNewIcon />
                </IconButton>
              </PaneTooltip>
            )}
            <PaneTooltip title={removeDatasourceTooltip(datasourceLabel)}>
              <IconButton
                onClick={() =>
                  showDialog({
                    title: removeDatasourceTitle(datasourceLabel),
                    description: DialogDescription(removeDatasourceDescription(datasourceLabel)),
                    alert: {
                      content: deleteNotice,
                    },
                    submit: {
                      label: REMOVE_ACTION,
                      destructive: true,
                      action: async () => {
                        hideDialog();
                        await onClose();
                      },
                    },
                    cancel: { action: hideDialog },
                  })
                }
              >
                <CloseIcon />
              </IconButton>
            </PaneTooltip>
          </Box>
        </Box>
        {onCardClick ? (
          <CardButton
            onClick={onCardClick}
            disableRipple
            sx={{
              flexDirection: "row",
              px: "12px",
              py: "8px",
              justifyContent: "space-between",
              gap: 1,
              "& .card-edit-icon": { visibility: "hidden" },
              "&:hover .card-edit-icon, &:focus-visible .card-edit-icon": { visibility: "visible" },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", textAlign: "left", minWidth: 0 }}>
              <HeaderText
                title={typeof card.text === "string" ? card.text : undefined}
                sx={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  // Without this a name typed as one unbroken string has no break opportunity, so the
                  // clamp has nothing to clamp and the line overflows anyway.
                  wordBreak: "break-word",
                }}
              >
                {card.text}
              </HeaderText>
              <BodyText noWrap>{card.subtext}</BodyText>
            </Box>
            {/* The pencil only appears once the card is hovered, so it uses the short delay — waiting
                again after it has already surfaced reads as broken. */}
            <PaneTooltip title={updateDatasourceTooltip(datasourceLabel)} enterDelay={INFO_TOOLTIP_DELAY_MS}>
              <EditIcon className="card-edit-icon" sx={{ fontSize: "18px", flexShrink: 0 }} />
            </PaneTooltip>
          </CardButton>
        ) : (
          <Box
            sx={{
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: "12px",
              py: "8px",
              minHeight: "54px",
              minWidth: 0,
              borderRadius: "8px",
              boxShadow: `inset 0 0 0 1px ${ediphiPalette.borderColor}`,
            }}
          >
            <HeaderText>{card.text}</HeaderText>
            <BodyText noWrap>{card.subtext}</BodyText>
          </Box>
        )}
        {children}
      </Card>
    </Box>
  );
};

export default DatasourceCardLayout;
